import { useCallback, useMemo, useState, ChangeEvent, forwardRef } from "react";

import MuiRadioGroup from "@mui/material/RadioGroup";
import { useTheme } from "@mui/material/styles";
import { FieldValues, Path, PathValue, useController } from "react-hook-form";

import { getLabel, isInputRequired } from "../../../helpers/validators";
import { useCurrentInputVibe } from "../../../hooks/useCurrentInputVibe";
import { DashboardIcon } from "../Icon/DashboardIcon";
import { DashboardOptionGroupLabel } from "../OptionGroupLabel";
import { DashboardTextField } from "../TextField/DashboardTextField";

import { Radio } from "./Radio";
import { searchSx } from "./styles";
import { RadioGroupProps, FormRadioGroupProps, BaseRadioGroupProps } from "./types";

import { useValidators } from "@/hooks/useValidators";
import { Search } from "@/icons/outlined";

export function DashboardRadioGroup<T extends FieldValues>({ control, rules, name, ...rest }: RadioGroupProps<T>) {
  return control && name ? (
    <FormRadioGroup control={control} rules={rules} name={name} {...rest} />
  ) : (
    <BaseRadioGroup {...rest} name={name} />
  );
}

function FormRadioGroup<T extends FieldValues>({
  control,
  defaultValue,
  name,
  onChange,
  onBlur,
  rules,
  alignContent,
  noAsterisk,
  ...rest
}: FormRadioGroupProps<T>) {
  const validators = useValidators(rules);
  const isRequired = isInputRequired(validators);

  const internalDefaultValue =
    defaultValue !== undefined ? { defaultValue: defaultValue as PathValue<T, Path<T>> } : undefined;

  const { field } = useController<T>({
    name,
    control,
    ...(internalDefaultValue ? internalDefaultValue : {}),
    rules: validators,
  });

  const handleBlur = useCallback(() => {
    onBlur?.();
    field.onBlur?.();
  }, [field, onBlur]);

  const handleChange = useCallback(
    (value: string) => {
      onChange?.(value);
      field.onChange?.(value);
    },
    [field, onChange]
  );

  return (
    <BaseRadioGroup
      {...rest}
      defaultValue={field.value as string}
      onBlur={handleBlur}
      onChange={handleChange}
      ref={field.ref}
      alignContent={alignContent}
      label={getLabel(isRequired, rest.label, noAsterisk)}
      name={name}
    />
  );
}

const BaseRadioGroup = forwardRef<unknown, BaseRadioGroupProps>(
  (
    {
      canSearch,
      className,
      defaultValue,
      disabled,
      disabledValues,
      error,
      errorMessage,
      helperText,
      label,
      vibe,
      onBlur,
      onChange,
      row,
      searchPlaceholder,
      size = "small",
      tooltipText,
      values,
      fullWidth,
      separator,
      "data-test": dataTest,
      alignContent,
      columns,
      name,
      tooltipTextValues,
    },
    ref
  ) => {
    const theme = useTheme();
    const currentInputVibe = useCurrentInputVibe(vibe);

    const [value, setValue] = useState(defaultValue);

    const handleChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setValue(value);
        onChange?.(value);
      },
      [onChange]
    );

    const [searchString, setSearchString] = useState("");

    const parsedValues: typeof values = useMemo(() => {
      if (!searchString || !canSearch) {
        return values;
      }
      const output: typeof values = {};
      Object.entries(values).forEach(([value, label]) => {
        if (label?.toString().toLowerCase().includes(searchString.toLowerCase())) {
          output[value] = label;
        }
      });
      return output;
    }, [canSearch, searchString, values]);

    const onTextChange = useCallback((event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setSearchString(event.target.value);
    }, []);

    const radioGroupStyles = useMemo(
      () => ({
        ...{ display: row ? "flex" : "block" },
        ...(fullWidth ? { width: "100%" } : undefined),
      }),
      [fullWidth, row]
    );

    const radio = (
      <DashboardOptionGroupLabel
        className={className}
        style={canSearch ? undefined : { width: "100%" }}
        vibe={currentInputVibe}
        data-test={dataTest}
        error={error}
        errorMessage={errorMessage}
        helperText={helperText}
        label={canSearch ? undefined : label}
        tooltipText={tooltipText}
      >
        <MuiRadioGroup
          row={row}
          value={value || ""}
          onChange={handleChange}
          onBlur={onBlur}
          ref={ref}
          style={radioGroupStyles}
          sx={{ alignItems: "center" }}
        >
          {Object.entries(parsedValues).map(([entryValue, entryLabel], index, allEntries) => {
            const title = typeof entryLabel === "string" ? entryLabel : entryValue;
            return (
              <>
                {index > 0 && separator}
                <Radio
                  key={entryValue}
                  style={{
                    marginTop: index !== 0 && !row ? theme.spacing(0.5) : undefined,
                    marginRight: index !== allEntries.length - 1 && row ? theme.spacing(2) : undefined,
                  }}
                  title={title}
                  size={size}
                  disabled={disabled || disabledValues?.[entryValue]}
                  vibe={currentInputVibe}
                  value={entryValue}
                  label={entryLabel}
                  row={row}
                  fullWidth={fullWidth}
                  alignContent={alignContent}
                  columns={columns}
                  tooltipText={tooltipTextValues?.[entryValue] || undefined}
                />
              </>
            );
          })}
        </MuiRadioGroup>
      </DashboardOptionGroupLabel>
    );

    if (!canSearch) {
      return radio;
    }
    return (
      <div className={className}>
        <DashboardTextField
          sx={searchSx}
          endAdornment={
            <DashboardIcon vibe="tertiary">
              <Search />
            </DashboardIcon>
          }
          label={label}
          onChange={onTextChange}
          placeholder={searchPlaceholder}
          size={size}
          value={searchString}
          name={name}
        />
        {radio}
      </div>
    );
  }
);

BaseRadioGroup.displayName = "BaseRadioGroup";
